import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Upload, FileText, CheckCircle, Activity, ShieldCheck, X, Building2 } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || '/api';

const HospitalClinicalSubmission = () => {
  const { id } = useParams();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [medicalReports, setMedicalReports] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [completed, setCompleted] = useState(false);
  
  const medicInputRef = useRef(null);
  const isSubmitting = useRef(false);

  useEffect(() => {
    const fetchLead = async () => {
      try {
        const res = await axios.get(`${API_URL}/leads/public/${id}`);
        const leadData = res.data.data;
        setLead(leadData);
        
        // 🔒 SECURITY: Enforce One-Time Use for patients, but ALLOW AGENT BYPASS
        // Check if current visitor is an authenticated Agent (Admin/CC)
        const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
        const isAgent = ['admin', 'customer_care', 'cc_head'].includes(storedUser.role);

        if (leadData.isClinicalLinkUsed && !isAgent) {
          setCompleted(true);
        }
      } catch (err) {
        toast.error(err.response?.data?.message || 'Submission link is invalid or has expired.');
      } finally {
        setLoading(false);
      }
    };
    fetchLead();
  }, [id]);

  const handleMedicalSelect = (e) => {
    const files = Array.from(e.target.files);
    setMedicalReports(prev => [...prev, ...files]);
    e.target.value = '';
  };

  const removeMedical = (idx) => setMedicalReports(prev => prev.filter((_, i) => i !== idx));

  const handleSubmit = async () => {
    if (isSubmitting.current) return;
    if (medicalReports.length === 0) return toast.error('Please upload at least one medical report.');

    isSubmitting.current = true;
    setUploading(true);
    try {
      // Upload Medical Reports
      const medFormData = new FormData();
      medicalReports.forEach(file => medFormData.append('documents', file));
      await axios.post(`${API_URL}/upload/public/${id}?type=medical_report`, medFormData);

      setCompleted(true);
      toast.success('Clinical documents submitted successfully');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Submission failed. Please try again.');
    } finally {
      isSubmitting.current = false;
      setUploading(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="w-12 h-12 border-4 border-[#143E78] border-t-transparent rounded-full animate-spin"></div>
    </div>
  );

  if (!lead) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6 text-center">
      <div className="max-w-md bg-white p-12 rounded-[40px] shadow-2xl border border-slate-100">
        <Activity className="w-16 h-16 text-red-400 mx-auto mb-6" />
        <h2 className="text-2xl font-black text-slate-800 uppercase italic tracking-tighter">Link Status</h2>
        <p className="text-slate-500 font-bold mt-2">This submission link is invalid or has expired.</p>
        <p className="text-xs text-slate-400 mt-4 font-bold uppercase tracking-widest px-4 leading-relaxed">Please request a fresh clinical protocol link from DOXEZ Customer Care.</p>
      </div>
    </div>
  );

  if (completed) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6 text-center">
      <div className="max-w-md bg-white p-12 rounded-[40px] shadow-2xl border border-slate-100 animate-fadeIn">
        <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <ShieldCheck className="w-10 h-10 text-emerald-600" />
        </div>
        <h2 className="text-3xl font-black text-[#143E78] uppercase italic tracking-tighter">Upload Complete</h2>
        <p className="text-slate-500 font-bold mt-4">Thank you.</p>
        <p className="text-xs text-slate-400 mt-4 font-bold px-4 leading-relaxed uppercase tracking-widest">Medical Reports for {lead.patientName} have been securely submitted to the DOXEZ clinical team.</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-2xl bg-white rounded-[40px] shadow-2xl border border-slate-100 overflow-hidden">
        <div className="bg-amber-600 p-10 text-center">
          <span className="inline-block px-4 py-1.5 bg-amber-800/20 text-amber-100 text-[10px] font-black rounded-full uppercase tracking-widest mb-4">Partner Upload Portal</span>
          <h1 className="text-3xl font-black text-white italic tracking-tighter mb-2">Patient: {lead.patientName}</h1>
          <p className="text-amber-100/80 font-bold text-sm">Action required: Upload {lead.patientName}'s Medical Reports</p>
        </div>

        <div className="p-10 space-y-10">
          {/* Diagnostic Protocol Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-xl bg-emerald-600 text-white flex items-center justify-center font-black text-[10px] italic shadow-lg shadow-emerald-100">MD</div>
              <h3 className="font-black text-slate-800 uppercase text-xs tracking-wider">Clinical Reports Submission</h3>
            </div>

            <div className="bg-slate-50 p-6 rounded-3xl border border-slate-100 space-y-4">
              <div className="grid grid-cols-1 gap-2 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
                {medicalReports.map((file, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3.5 bg-white rounded-2xl border border-slate-100 shadow-sm">
                    <div className="flex items-center gap-3 overflow-hidden">
                      <FileText className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                      <span className="text-xs font-black text-slate-700 truncate">{file.name}</span>
                    </div>
                    <button onClick={() => removeMedical(idx)} className="p-1.5 text-slate-300 hover:text-red-500 transition-all">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                {medicalReports.length === 0 && (
                  <div className="py-10 text-center opacity-40">
                    <Upload className="w-8 h-8 mx-auto mb-2" />
                    <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">No reports added yet</p>
                  </div>
                )}
              </div>
              <button onClick={() => medicInputRef.current.click()} className="w-full py-4 text-[10px] font-black text-emerald-600 uppercase tracking-widest bg-white hover:bg-emerald-50 rounded-2xl transition-all border-2 border-dashed border-emerald-100">
                + Add Scan / PDF / Image
              </button>
            </div>
            <input type="file" hidden ref={medicInputRef} onChange={handleMedicalSelect} multiple accept="image/*,.pdf" />
          </div>

          <button onClick={handleSubmit} disabled={uploading || medicalReports.length === 0} className="w-full bg-amber-600 text-white font-black py-6 rounded-[30px] shadow-2xl shadow-amber-900/20 hover:scale-[1.02] active:scale-95 transition-all uppercase text-sm tracking-widest disabled:opacity-50">
            {uploading ? 'Initiating Secure Submission...' : 'Confirm & Submit Reports'}
          </button>

          <div className="pt-8 border-t border-slate-50 flex items-center justify-center gap-10 opacity-30">
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-amber-600" />
              <span className="text-[9px] font-black uppercase text-slate-500 tracking-tighter">Secure Link</span>
            </div>
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-amber-600" />
              <span className="text-[9px] font-black uppercase text-slate-500 tracking-tighter">Diagnostic Partner</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HospitalClinicalSubmission;
